import React from "react";
import { Undo } from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import { greys, colors } from "@styles/color.styles";
import { fontFamilyHelvetica } from "@styles/type.styles";
import { ISuggestedSlotsByDate } from "../../scheduling-link";
import { DateTime } from "luxon";
import { DateString } from "@ui/scheduling-link/scheduling-link";

const useStyles = makeStyles({
  earliestBtn: {
    display: "flex",
    alignItems: "center",
    padding: "5px 12px",
    borderRadius: 30,
    backgroundColor: greys.white,
    marginLeft: 6,
    marginRight: 6,
    boxShadow: "none",
    border: `1px solid ${greys.cloudyBlue}`,
    color: colors.blueDusk.darker,
    fontFamily: fontFamilyHelvetica,
    fontSize: `${14 / 16}rem`,
    cursor: "pointer",
    whiteSpace: "nowrap",

    "&:hover": {
      backgroundColor: greys.palePaleGrey,
    },

    "&:disabled": {
      cursor: "unset",
      backgroundColor: greys[1],
      color: greys[2],
      borderColor: greys[2],
    },
  },
});

const findEarliestDateOrNow = (days: ISuggestedSlotsByDate[]) => {
  const day = days.find((day) => day.suggestedSlots.length);
  return day?.date ? day.date : DateTime.now().toISODate();
};

export type ShowEarliestButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  currentDate?: string;
  disabled?: boolean;
  days: ISuggestedSlotsByDate[];
  onShowEarliest: (date: DateString) => void;
};

const ShowEarliestButton = ({
  currentDate = "",
  disabled = false,
  days,
  onShowEarliest,
  ...buttonProps
}: ShowEarliestButtonProps) => {
  const classes = useStyles();

  const earliestDate = React.useMemo(() => findEarliestDateOrNow(days), [days]);

  const handleShowEarliest = React.useCallback(() => onShowEarliest(earliestDate), [
    earliestDate,
    onShowEarliest,
  ]);

  const isDisabled = disabled || currentDate === earliestDate;

  return (
    <button
      {...buttonProps}
      aria-label="scroll to earliest available times"
      className={classes.earliestBtn}
      cw-id="link-show-earliest"
      key="link-show-earliest"
      onClick={handleShowEarliest}
      disabled={isDisabled}
    >
      <Undo className="cw-mr-2" />
      <span>Show Earliest</span>
    </button>
  );
};

export default ShowEarliestButton;
